import { useRef, useState } from "react";

import { useQuery, UseQueryOptions } from "react-query";

interface PaginationOptions<R, F> {
  queryKey: string;
  defaultPage?: number;
  defaultLimit?: number;
  defaultRestQueryParams?: F;
  defaultEnable?: boolean;
  onError?: UseQueryOptions<R>["onError"];
  onSuccess?: UseQueryOptions<R>["onSuccess"];
  onSettled?: UseQueryOptions<R>["onSettled"];
}

const usePagination = <P, R, F>(
  fnc: (p: P) => Promise<R>,
  {
    queryKey,
    defaultPage = 1,
    defaultLimit = 5,
    defaultRestQueryParams,
    defaultEnable = true,
    onError,
    onSuccess,
    onSettled,
  }: PaginationOptions<R, F>
) => {
  const enableFlag = useRef(defaultEnable);
  const [page, setPage] = useState(defaultPage);
  const [limit, setLimit] = useState(defaultLimit);
  const [restQueryParams, setRestQueryParams] = useState<F | null>(
    defaultRestQueryParams || null
  );

  const queryResult = useQuery(
    [queryKey, { page, limit, ...(restQueryParams || {}) }],
    ({ queryKey: [, params] }) => fnc(params as unknown as P & F),
    {
      enabled: enableFlag.current,
      keepPreviousData: true,
      onError,
      onSuccess,
      onSettled,
    }
  );

  const updateEnableFlag = (enable = true) => {
    enableFlag.current = enable;
  };

  const resetPage = () => setPage(1);

  const onLimitChangeHandler = (value: number) => {
    enableFlag.current = true;
    setLimit(value);
    resetPage();
  };

  const updateRestQueryParams = (
    value: typeof restQueryParams,
    replaceAll?: boolean
  ) => {
    if (value === null) throw new Error("The value should be not NULL!");
    enableFlag.current = true;
    setRestQueryParams((prevState) =>
      replaceAll ? { ...value } : { ...(prevState || {}), ...value }
    );
    resetPage();
  };

  const reset = () => {
    setPage(1);
    setLimit(defaultLimit);
    setRestQueryParams(defaultRestQueryParams || null);
  };

  return {
    page,
    limit,
    restQueryParams,
    updateRestQueryParams,
    onPageChangeHandler: setPage,
    onLimitChangeHandler,
    reset,
    updateEnableFlag,
    ...queryResult,
  };
};

export default usePagination;
